import React, { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { api } from '../lib/api';

// Item 3 (Dan, 2026-09-21): door-to-door reps carry a QR code that opens
// the plan page with ?rep=<their-slug> already in the URL. The WordPress
// checkout plugin reads that param, carries it through to Stripe as
// subscription metadata, and stripeSyncService.resolveRepIdFromCode()
// resolves it back against sales_reps.email (the part before @) to set
// origin_channel='sales_credit' + attributed_rep_id on the order. This
// page is the self-service source for those codes: it reads the same
// sales_reps table everything else uses, so a rep added/removed there
// (GET /reps, synced from the Google Workspace group, or added manually)
// shows up or disappears here automatically -- no one has to come back
// and regenerate a one-off image by hand.
//
// PLAN_URL must match pp_checkout_setting('plan_url') in the WordPress
// plugin (Settings > Prickly Pear Checkout in wp-admin) -- the two aren't
// wired together, so if that URL ever changes on the WordPress side,
// update it here too or these codes will point at a stale page.
const PLAN_URL = 'https://pricklypearwildlife.com/pest-control-plans/';

function repSlug(email) {
  if (!email) return '';
  return email.split('@')[0].trim().toLowerCase();
}

function repUrl(email) {
  const slug = repSlug(email);
  return slug ? `${PLAN_URL}?rep=${encodeURIComponent(slug)}` : '';
}

function RepQrCard({ rep }) {
  const canvasRef = useRef(null);
  const slug = repSlug(rep.email);
  const url = repUrl(rep.email);

  function download() {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `prickly-pear-rep-qr-${slug || rep.id}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  return (
    <div className="card" style={{ maxWidth: 280 }}>
      <h3 style={{ marginTop: 0, marginBottom: 4 }}>{rep.name}</h3>
      <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 0 }}>{rep.email}</p>
      {slug ? (
        <>
          <div ref={canvasRef} style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
            <QRCodeCanvas value={url} size={180} level="M" includeMargin />
          </div>
          <p style={{ fontSize: 12, wordBreak: 'break-all', color: 'var(--color-text-muted)' }}>{url}</p>
          <button type="button" className="btn btn-primary" onClick={download} style={{ width: '100%' }}>
            Download PNG
          </button>
        </>
      ) : (
        <p className="hint-text">No email on file -- can't build a rep code for this person.</p>
      )}
    </div>
  );
}

export default function Reps() {
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get('/reps');
      setReps(result.reps || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <div>
      <h2>Rep QR Codes</h2>
      {error && <div className="error-banner">{error}</div>}
      <p className="hint-text">
        One QR code per active rep, built from the same {' '}
        <code>{PLAN_URL}?rep=&lt;slug&gt;</code> pattern the WordPress checkout page and CSR Manager
        already both understand. Scanning one takes a customer straight to the plan page with that
        rep's code pre-filled, so any subscription they sign up for gets credited automatically.
        This list is pulled live from your sales reps (Google Workspace group sync, plus anyone
        added manually) -- add or remove a rep there and this page follows.
      </p>
      {loading ? (
        <p>Loading…</p>
      ) : reps.length === 0 ? (
        <p>No active reps found.</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          {reps.map((rep) => (
            <RepQrCard key={rep.id} rep={rep} />
          ))}
        </div>
      )}
    </div>
  );
}
